<script setup lang="ts">
import { ref, watch, computed, reactive } from 'vue'
import { useTFACurtailmentIntervals } from '@/composables/cpm/cpmProgramManagement'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import type { ITFACurtailmentIntervalFilters, ITFACurtailmentIntervalRatioFilters } from '@/types/cpm/filters'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import type { ICustomer } from '@/types/core/Customer'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useOIDCStore } from '@/stores/oidc.store'
import CPMIntervalsTable from '@/components/cpm/CPMIntervalsTable.vue'
import CPMAddIntervalDialog from '@/components/cpm/CPMAddIntervalDialog.vue'

const { isAdminUser } = useOIDCStore()

const filters = reactive<ITFACurtailmentIntervalFilters>({
  site_id: undefined,
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  page: 1,
  per_page: 10,
})

const { getCurtailmentRatioQuery } = useTFACurtailmentIntervals()

const ratioFilters = computed<ITFACurtailmentIntervalRatioFilters>(() => ({
  site_id: filters.site_id,
  year: filters.year,
  month: filters.month,
}))

const { data: ratioData, isLoading: isRatioLoading } = getCurtailmentRatioQuery(ratioFilters)

// Selected customer object (for RcCustomerSelect)
const selectedCustomer = ref<ICustomer | undefined>(undefined)

// Selected site object (for RcSiteSelect)
const selectedSite = ref<ISiteIdentifier | undefined>(undefined)

// Month and Year selectors
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

const showAddModal = ref(false)

// Reference to the CPMIntervalsTable component
const intervalsTableRef = ref<InstanceType<typeof CPMIntervalsTable> | null>(null)

// Watch for changes in selectedCustomer to update the filters customerId
watch(selectedCustomer, () => {
  // Reset site selection when customer changes
  selectedSite.value = undefined
  filters.site_id = undefined
})

// Watch for changes in selectedSite to update the filters siteId
watch(selectedSite, (newSite) => {
  filters.site_id = newSite?.site_id
})

const updateFilters = (newFilters: Partial<ITFACurtailmentIntervalFilters>) => {
  Object.assign(filters, newFilters)
}

const displayTimezone = computed(() => selectedSite.value?.timezone ?? 'UTC')
const isTimezoneFromSite = computed(() => !!selectedSite.value?.timezone)
</script>

<template>
  <ViewLayout>
    <template #controls>
      <el-button v-if="isAdminUser" type="primary" @click="showAddModal = true">Add Curtailment Interval</el-button>
    </template>

    <div class="flex flex-col gap-6 flex-grow-1">
      <div class="flex gap-3 flex-wrap">
        <RcCustomerSelect v-model="selectedCustomer" class="w-[300px]" placeholder="Customer" />
        <RcSiteSelect
          v-model="selectedSite"
          :disabled="!selectedCustomer"
          class="w-[300px]"
          placeholder="Site"
          :customer-id="selectedCustomer?.id"
        />
      </div>

      <el-card class="w-full flex flex-col flex-grow-1">
        <div class="flex justify-between items-center mb-4">
          <el-text type="info">HISTORICAL AND FUTURE CURTAILMENTS</el-text>
          <el-tooltip content="Curtailed / Recommended" placement="top">
            <div
              class="flex items-center justify-center py-1 px-2 bg-[var(--el-color-info-light-9)] rounded-xl text-2xl font-500"
            >
              <template v-if="ratioData"> {{ ratioData.curtailed_hours }}/{{ ratioData.total_hours }} </template>
              <template v-else>
                <span v-if="isRatioLoading">-/-</span>
                <span v-else>0/0</span>
              </template>
            </div>
          </el-tooltip>
        </div>

        <div class="flex gap-3 mb-4">
          <el-select v-model="filters.month" placeholder="Month" class="w-[160px]">
            <el-option v-for="(monthName, index) in months" :key="index" :label="monthName" :value="index + 1" />
          </el-select>

          <el-select v-model="filters.year" placeholder="Year" class="w-[160px]">
            <el-option v-for="year in years" :key="year" :label="year.toString()" :value="year" />
          </el-select>
        </div>

        <el-text v-if="selectedSite" type="info" class="block mb-3" size="small">
          <template v-if="isTimezoneFromSite">
            Intervals are shown in the site's timezone: <strong>{{ displayTimezone }}</strong>
          </template>
          <template v-else> Intervals are shown in <strong>UTC</strong> (site has no timezone set). </template>
        </el-text>

        <CPMIntervalsTable
          ref="intervalsTableRef"
          :filters="filters"
          :site-timezone="selectedSite?.timezone ?? 'UTC'"
          @update-filters="updateFilters"
        />
      </el-card>
    </div>

    <CPMAddIntervalDialog
      v-if="showAddModal"
      v-model="showAddModal"
      :selected-customer="selectedCustomer"
      :selected-site="selectedSite"
    />
  </ViewLayout>
</template>

<style lang="scss" scoped>
:deep(.el-card__body) {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
</style>
